// HeyCatch analytics - privacy-guarded wrapper.
//
// WHY THIS FILE EXISTS
// The HeyCatch SDK is initialised once in src/index.js (module scope, per the
// vendor's install guide). Everything else in the app talks to HeyCatch through
// THIS module only, so the privacy contract below lives in exactly one
// auditable place instead of being re-decided at every call site.
//
// PRIVACY CONTRACT (agreed with the product owner - do not widen without asking)
// The ONLY person data we ever send is:
//   * the internal application user id (an opaque UUID - never an email, never
//     an email-derived hash, never a session token)
//   * a coarse billing tier: "free" | "weekly" | "monthly" | "yearly"
// We deliberately DO NOT send: email addresses, display or legal names, phone
// numbers, recovery program / fellowship, account type (sponsor vs sponsee),
// sponsorship relationships, message contents, recovery history, payment or
// card details, or free-message balances.
//
// Note that `access_label` is intentionally NOT forwarded either: it can read
// "Recovery Access Pass", which would disclose that a member could not afford
// the subscription.
//
// Anonymous visitors stay anonymous: setIdentity is only ever called once a
// member is authenticated, and resetIdentity runs on sign-out so one person's
// analytics identity can never carry over to the next person on a shared
// device.
//
// AUTOCAPTURE
// The SDK already captures pageviews, clicks and SPA route changes on its own
// (it patches history.pushState/replaceState and listens for popstate), so we
// never hand-wire clicks or generic pageviews here. The only route-level events
// in this file are the five named funnel entry points in ROUTE_VIEW_EVENTS,
// which exist so those steps can be selected by name when building funnels.
import { analytics } from "@heycatch/sdk";

import { devWarn } from "./logger";

/**
 * The complete set of business outcomes we report. Names intentionally mirror
 * the app's own first-party funnel events (src/lib/analytics.js) so the Sponsor
 * Me admin dashboard and the HeyCatch dashboard describe the same funnel.
 */
export const HEYCATCH_EVENTS = Object.freeze({
  SIGNUP_STARTED: "signup_started",
  SIGNUP_COMPLETED: "signup_completed",
  SPONSOR_PROFILE_COMPLETED: "sponsor_profile_completed",
  SUBSCRIPTION_STARTED: "subscription_started",
  FIRST_MESSAGE_SENT: "first_message_sent",
  SPONSORSHIP_REQUESTED: "sponsorship_requested",
  // Funnel entry points. Autocapture already records a generic pageview for
  // EVERY route; these named events exist only so the five pages that matter to
  // the business funnel can be used as explicit funnel steps in HeyCatch
  // (Home -> Register -> signup_started, Home -> Pricing -> subscription_started,
  // Home -> Sponsors -> sponsorship_requested, and the About/Contact reading
  // paths). No extra custom event is added for any other route, so nothing is
  // double-counted beyond these deliberate five.
  REGISTER_VIEWED: "register_viewed",
  PRICING_VIEWED: "pricing_viewed",
  SPONSORS_VIEWED: "sponsors_viewed",
  CONTACT_VIEWED: "contact_viewed",
  ABOUT_VIEWED: "about_viewed",
  // Homepage positioning CTAs. These measure whether the reworked hero and the
  // How It Works journey actually move people into the directory or into
  // volunteering. They carry no properties at all - which button was pressed is
  // the entire signal, and nothing about the visitor is attached.
  HERO_BROWSE_SPONSORS_CLICKED: "hero_browse_sponsors_clicked",
  HERO_BECOME_SPONSOR_CLICKED: "hero_become_sponsor_clicked",
  HOW_IT_WORKS_BROWSE_CLICKED: "how_it_works_browse_clicked",
  // Trust-signal interactions on the homepage / footer. No properties.
  FOUNDER_STORY_CLICKED: "founder_story_clicked",
  TRUST_SAFETY_VIEWED: "trust_safety_viewed",
  BECOME_SPONSOR_EMPTY_STATE_CLICKED: "become_sponsor_empty_state_clicked",
  // Someone asked to be told when a sponsor becomes available, from a
  // zero-result directory search. Carries NO properties: the email address and
  // the chosen recovery pathway are health-adjacent personal data and must
  // never reach a third party, so they are not sent - not even hashed.
  AVAILABILITY_ALERT_REQUESTED: "availability_alert_requested",
  PRODUCT_UPDATES_CLICKED: "product_updates_clicked",
  // Informational comparison page (/vs/ai-sponsor). These carry no properties:
  // they record that the page was seen and which CTA was chosen, nothing about
  // who the visitor is.
  VS_AI_SPONSOR_VIEWED: "vs_ai_sponsor_viewed",
  VS_AI_BROWSE_SPONSORS_CLICKED: "vs_ai_browse_sponsors_clicked",
  VS_AI_BECOME_SPONSOR_CLICKED: "vs_ai_become_sponsor_clicked",
});

const ALLOWED_EVENTS = new Set(Object.values(HEYCATCH_EVENTS));

/**
 * Exact React Router paths (see src/App.js) that get a named funnel event.
 * Keyed on the real route names in this app - note pricing lives at /pricing,
 * so there is a single `pricing_viewed` event and no `plans_viewed` twin.
 * Paths are matched exactly, so parameterised pages such as /sponsors/:userId
 * deliberately do NOT emit `sponsors_viewed` (that is a profile view, not the
 * directory), and are still covered by autocapture.
 */
export const ROUTE_VIEW_EVENTS = Object.freeze({
  "/register": HEYCATCH_EVENTS.REGISTER_VIEWED,
  "/pricing": HEYCATCH_EVENTS.PRICING_VIEWED,
  "/sponsors": HEYCATCH_EVENTS.SPONSORS_VIEWED,
  "/contact": HEYCATCH_EVENTS.CONTACT_VIEWED,
  "/about": HEYCATCH_EVENTS.ABOUT_VIEWED,
});

// Stripe lookup key -> coarse, non-sensitive billing tier.
const PLAN_TIERS = Object.freeze({
  sponsor_me_weekly: "weekly",
  sponsor_me_monthly: "monthly",
  sponsor_me_yearly: "yearly",
});

/**
 * Coarse billing tier for a member. Returns "free" for members with no paid
 * plan (including sponsors, who never pay) and null when we have no user.
 */
export function planTier(user) {
  if (!user) return null;
  return PLAN_TIERS[user.subscription_plan] || "free";
}

// Remembers the last identity we sent so repeated /auth/me refreshes (which run
// on every boot and after most mutations) do not re-send an unchanged identity.
let lastIdentityKey = null;

/**
 * Attach analytics to a signed-in member. Safe to call on every auth refresh.
 * Sends the internal user id plus the coarse billing tier - nothing else.
 */
export function identifyMember(user) {
  if (!user?.id) return;
  const tier = planTier(user);
  const key = `${user.id}:${tier}`;
  if (key === lastIdentityKey) return;
  lastIdentityKey = key;
  try {
    analytics.setIdentity(user.id, { plan: tier });
  } catch (err) {
    // Analytics must never break the app.
    devWarn("heycatch.setIdentity", err);
  }
}

/**
 * Return the visitor to anonymous on sign-out, so the next person to use this
 * device is not attributed to the member who just left.
 */
export function resetMember() {
  lastIdentityKey = null;
  try {
    analytics.resetIdentity();
  } catch (err) {
    devWarn("heycatch.resetIdentity", err);
  }
}

/**
 * Report a business outcome. `props` is filtered to primitives and must only
 * ever carry non-sensitive values - see the privacy contract above.
 */
export function trackOutcome(name, props) {
  if (!ALLOWED_EVENTS.has(name)) {
    devWarn("heycatch.unknownEvent", name);
    return;
  }
  // Development-only mirror of what we asked HeyCatch to record. It exists so
  // duplicate-event bugs can be proven in a browser session ("did this fire
  // once per navigation or twice?") without trusting the dashboard's own
  // latency. It is compiled out of production builds, holds the last 50 event
  // names only, and never stores anything the event itself does not already
  // carry - which is never personal data (see the privacy contract above).
  if (process.env.NODE_ENV !== "production" && typeof window !== "undefined") {
    window.__smEvents = (window.__smEvents || []).slice(-49);
    window.__smEvents.push({ name, props: props || null, at: Date.now() });
  }
  try {
    analytics.trackEvent(name, props);
  } catch (err) {
    devWarn("heycatch.trackEvent", err);
  }
}

function guardedTrack(store, guardKey, name, props) {
  let alreadySent = false;
  try {
    alreadySent = store.getItem(guardKey) === "1";
    if (!alreadySent) store.setItem(guardKey, "1");
  } catch (err) {
    // Private mode / blocked storage: fall through and send. The event may then
    // repeat across reloads, which is far better than losing the conversion.
    devWarn("heycatch.onceGuard", err);
  }
  if (!alreadySent) trackOutcome(name, props);
}

/**
 * Send an outcome at most once per browser session. Used for funnel steps a
 * member can legitimately revisit (e.g. stepping back and forth through the
 * signup form) where re-renders or navigation would otherwise double-count.
 */
export function trackOutcomeOncePerSession(guard, name, props) {
  guardedTrack(window.sessionStorage, `hc_once_s:${guard}`, name, props);
}

/**
 * Send an outcome at most once ever on this device. Used for true one-time
 * conversions so a page refresh can never inflate them. `guard` should include
 * whatever makes the outcome unique (a user id, a Stripe session id, ...).
 */
export function trackOutcomeOnce(guard, name, props) {
  guardedTrack(window.localStorage, `hc_once:${guard}`, name, props);
}

// Last navigation we reported, held at MODULE scope (not in a component ref) so
// it survives React re-renders, Strict Mode's double-invoked effects and any
// remount of the tracking component. Only a genuinely new history entry /
// pathname pair produces a new token, so:
//   * re-renders and state changes        -> same token -> not sent again
//   * Strict Mode double effect on mount  -> same token -> sent exactly once
//   * real navigation (link, redirect)    -> new history key -> sent
//   * browser back / forward              -> different key than the one we just
//                                            reported -> sent (a real view)
//   * full page load or refresh           -> fresh module, token null -> sent
let lastRouteToken = null;

/**
 * Report a named funnel view for the current React Router location, exactly
 * once per real navigation.
 *
 * @param {string} pathname   location.pathname (never location.search - query
 *                            strings are not forwarded, see privacy contract).
 * @param {string} navKey     location.key, React Router's per-history-entry id.
 * @param {{authenticated?: boolean}} [props]
 */
export function trackRouteView(pathname, navKey, props) {
  const name = ROUTE_VIEW_EVENTS[pathname];
  if (!name) return;
  const token = `${navKey || "initial"}|${pathname}`;
  if (token === lastRouteToken) return;
  lastRouteToken = token;
  // `route` is the static route pattern we matched - no ids, no query string,
  // no search terms or filter values. `authenticated` is a boolean only.
  trackOutcome(name, {
    route: pathname,
    ...(props?.authenticated === undefined
      ? {}
      : { authenticated: Boolean(props.authenticated) }),
  });
}
